import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { NotificationService } from 'app/shared/services/notification.service';
import { BusinessUnit } from 'app/shared/models/business-unit.model';
import { BusinessUnitsService } from 'app/shared/services/business-units.service';
import { take } from 'rxjs/operators';
import { Constants } from 'app/shared/config/constants';
import {
  ClientService,
  UserService
} from 'app/shared/services';
import { SafeHtml } from "@angular/platform-browser";

@Component ( {
    selector: 'reporter-business-unit-tree',
    templateUrl: './business-unit-tree.component.html',
    styleUrls: ['./business-unit-tree.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [NotificationService],
    standalone: false
} )
export class BusinessUnitTreeComponent implements OnInit, OnChanges {

  @Input () active = false;
  @Input () reportingCapable = false;
  @Input () buId: string;
  @Input () rootBuId: string;
  @Input () activeDate: string;
  @Output () selectBU = new EventEmitter<BusinessUnit> ();

  rootLevel: BusinessUnit[];
  children: BusinessUnit[];
  levels: BusinessUnit[][];
  selected: BusinessUnit;
  inFocus: BusinessUnit;
  inputId: string;
  pageNumber = 1;
  totalPages = 1;
  buDetails: any;
  searchTerm = '';
  searchFieldValue: string;
  timeOut;
  selectedOnHover = false;
  buReferenceNumberPrompt: string;
  systemClientSelected: boolean;
  showLevels = true;
  private initialized = false;

  constructor(
    private businessUnitsService: BusinessUnitsService,
    private clientService: ClientService,
    private userService: UserService
  ) {
    this.systemClientSelected = this.userService.currentClient?.clientNumber?.toUpperCase() === 'SYSTEM';
  }

  ngOnInit(): void {
    this.buReferenceNumberPrompt =
      this.clientService.buTextSettingsByKey ( 'BU_REFERENCE_NUMBER_PROMPT' ) || Constants.buReferenceNumberPrompt;
    this.initialized = true;
    if ( this.rootBuId || !this.buId ) {
      this.loadRootLevel ();
    } else {
      this.loadBusinessUnitAncestry ( this.buId );
    }
  }

  ngOnChanges( changes: SimpleChanges ) {
    if ( !this.initialized ) {
      return;
    }

    if ( changes?.rootBuId && changes.rootBuId.currentValue !== changes.rootBuId.previousValue ) {
      this.loadRootLevel ();
      return;
    }

    if ( this.rootBuId ) {
      return;
    }

    if ( changes?.buId && changes.buId?.currentValue && changes.buId.currentValue !== changes.buId.previousValue ) {
      this.loadBusinessUnitAncestry ( this.buId );
    }
    if ( changes?.buId && !changes.buId?.currentValue ) {
      this.selected = undefined;
      this.levels = undefined;
      this.children = undefined;
      this.inFocus = undefined;
      this.buDetails = undefined;
      this.ngOnInit ();
    }
  }

  private loadBusinessUnitAncestry ( businessUnitId: string ): void {
    this.inputId = businessUnitId;
    this.businessUnitsService.getBUAncestors ( businessUnitId )
      .pipe ( take ( 1 ) )
      .subscribe ( result => {
        this.levels = result.businessUnitAncestry.map ( bu => [ bu ] );
        if ( !this.levels.length ) {
          this.businessUnitsService.getStartingLocations ()
            .pipe ( take ( 1 ) )
            .subscribe ( bu => {
              this.rootLevel = bu;
              this.levels = [ this.rootLevel ];
            } );
        } else {
          this.showLevels = false;
          setTimeout ( () => {
            this.levels.push ( [ result.selectedBusinessUnit ] );
            this.showLevels = true;
          }, 100 );
        }
        this.selected = result.selectedBusinessUnit;
        this.businessUnitsService.getBUDetails ( this.selected.id )
          .toPromise ().then ( res => {
          this.buDetails = res;
        } );
        this.children = undefined;
      } );
  }

  private loadRootLevel (): void {
    this.initChildren ();

    if ( this.rootBuId ) {
      this.businessUnitsService.getBUDetails ( this.rootBuId )
        .pipe ( take ( 1 ) )
        .subscribe ( bu => {
          this.rootLevel = [ bu ];
          this.levels = [ this.rootLevel ];
          this.selected = bu;
          this.buDetails = bu;
          this.inFocus = undefined;
          this.loadChildren ();
        } );
      return;
    }

    this.businessUnitsService.getStartingLocations ()
      .pipe ( take ( 1 ) )
      .subscribe ( bu => {
        this.rootLevel = bu;
        this.levels = [ this.rootLevel ];
      } );
  }

  select( bu: BusinessUnit, currentLevel: number ) {
    this.inputId = undefined;

    if ( this.selected?.id === bu.id && this.children ) {
      return;
    }

    if ( currentLevel < this.levels?.length - 1 ) {
      this.levels = this.levels.slice ( 0, currentLevel + 1 );
      this.pageNumber = 1;
    }
    this.selected = bu;
    this.initChildren ();
    this.loadChildren ();
  }

  refineSearch() {
    this.searchTerm = this.searchFieldValue;
    this.initChildren ();
    this.loadChildren ();
  }

  selectChild( child: BusinessUnit ) {
    if ( child.hasChildren ) {
      this.searchFieldValue = undefined;
      this.searchTerm = undefined;
      this.levels.push ( [ child ] );
      this.selected = child;
      this.initChildren ();
      this.loadChildren ();
    }
  }

  loadChildren() {
    if ( !this.selected.hasChildren ) {
      return;
    }
    this.businessUnitsService
      .getBusinessUnitChildren (
        this.selected.id,
        this.reportingCapable,
        this.searchTerm,
        this.pageNumber,
        Constants.maxPageSize,
        this.activeDate )
      .pipe ( take ( 1 ) )
      .subscribe ( result => {
        this.totalPages = result.totalPages;
        if ( this.pageNumber === 1 ) {
          this.children = result.records;
        } else {
          this.children = this.children.concat ( result.records );
        }
        this.pageNumber++;
      } );
  }

  initChildren() {
    this.children = undefined;
    this.pageNumber = 1;
    this.totalPages = 1;
  }

  emitSelected( bu: BusinessUnit ) {
    this.levels = [ this.rootLevel ];
    if ( bu?.id.toUpperCase() !== 'SYSTEM' && ( !this.reportingCapable || bu?.reportingLocation ) ) {
      this.selectBU.emit ( bu );
    }
    if ( !bu ) {
      this.selectBU.emit ( null );
    }
    this.initChildren ();
    this.selected = undefined;
    this.inFocus = undefined;
    this.searchFieldValue = undefined;
    this.searchTerm = undefined;
  }

  highlightText( text: string ): SafeHtml {
    if ( this.searchTerm ) {
      const regex = new RegExp ( `(${ this.searchTerm })`, 'gi' );
      const highlighted = text.replace ( regex, '<span class="highlight">$1</span>' );
      return highlighted as unknown as SafeHtml;
    }
    return text as unknown as SafeHtml;
  }
}
